import { EVIDENCE_DISPOSITION_FIELDS } from './evidenceFields';

export const RULE_COLORS = ['#1976d2', '#e8590c', '#2f9e44', '#9c36b5', '#0c8599', '#e03131'];

export const RULE_OP_CODES = {
  EQ: 'eq',
  NEQ: 'neq',
  GT: 'gt',
  LT: 'lt',
  GTE: 'gte',
  LTE: 'lte',
  CONTAINS: 'contains',
};

export const OP_SYMBOLS = {
  [RULE_OP_CODES.EQ]: '=',
  [RULE_OP_CODES.NEQ]: '≠',
  [RULE_OP_CODES.GT]: '>',
  [RULE_OP_CODES.LT]: '<',
  [RULE_OP_CODES.GTE]: '≥',
  [RULE_OP_CODES.LTE]: '≤',
  [RULE_OP_CODES.CONTAINS]: 'contains',
};

export const RULE_OPS_SELECT = [
  { value: RULE_OP_CODES.EQ, label: 'Equals' },
  { value: RULE_OP_CODES.NEQ, label: 'Not Equals' },
];
export const RULE_OPS_NUMBER = [
  { value: RULE_OP_CODES.GT, label: 'Greater Than' },
  { value: RULE_OP_CODES.LT, label: 'Less Than' },
  { value: RULE_OP_CODES.GTE, label: 'Greater Or Equal' },
  { value: RULE_OP_CODES.LTE, label: 'Less Or Equal' },
  { value: RULE_OP_CODES.EQ, label: 'Equals' },
];

export const CONDITION_LOGIC = {
  AND: 'AND',
  OR: 'OR',
};

export const RULE_FIELD_TYPES = {
  SELECT: 'select',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
};

// Static Yes/No options for boolean-type rule fields (e.g. ReadyForDisposal) — not dictionary-
// sourced, since this isn't a real code table. value is what's stored in ValueID and matched
// against the evidence item's boolean field (via getFieldValue's true/false -> Yes/No mapping);
// label is what's stored in Value and shown in the dialog/condition summary.
export const RULE_BOOLEAN_VALUE_OPTIONS = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 0 },
];

export const RULE_FIELD_CONFIG = [
  {
    value: 'RequiredAction',
    label: 'Required Action',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'requiredAction',
  },
  {
    value: 'Category',
    label: 'Category',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'category',
  },
  {
    value: 'PropertyType',
    label: 'Property Type',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'propertyType',
  },
  {
    value: 'EvidenceType',
    label: 'Evidence Type',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'evidenceType',
  },
  {
    value: 'Status',
    label: 'Status',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'custodyStatus',
  },
  {
    value: 'StorageLocation',
    label: 'Storage Location',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'storageLocation',
  },
  {
    value: 'SubstanceCategory',
    label: 'Substance Category',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'substanceCategory',
  },
  {
    value: 'DrugType',
    label: 'Drug Type',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'drugType',
  },
  {
    value: 'DisposalCode',
    label: 'Disposal Code',
    type: RULE_FIELD_TYPES.SELECT,
    dropdownKey: 'disposalCode',
  },
  {
    value: 'DaysInStorage',
    label: 'Days Since Check-In',
    type: RULE_FIELD_TYPES.NUMBER,
  },
  {
    value: 'ReadyForDisposal',
    label: 'Ready for Disposal',
    type: RULE_FIELD_TYPES.BOOLEAN,
  },
];

export const DISPOSAL_COLUMN_IDS = {
  EVIDENCE_ID: 'EvidenceID',
  INCIDENT_NUMBER: 'IncidentNumber',
  DESCRIPTION: 'Description',
  CATEGORY: 'Category',
  STATUS: 'Status',
  CURRENT_LOCATION: 'CurrentLocation',
  DAYS: '_days',
  MATCHED_RULES: '_matchedRules',
  REQUIRED_ACTION: 'RequiredAction',
};

export const DISPOSAL_COLUMNS = [
  {
    id: DISPOSAL_COLUMN_IDS.EVIDENCE_ID,
    field: 'EvidenceID',
    label: 'Evidence ID',
    defaultWidth: 140,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.INCIDENT_NUMBER,
    field: 'IncidentNumber',
    label: 'Incident Number',
    defaultWidth: 140,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.DESCRIPTION,
    field: 'Description',
    label: 'Description',
    defaultWidth: 220,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.CATEGORY,
    field: 'Category',
    label: 'Category',
    defaultWidth: 130,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.STATUS,
    field: 'Status',
    label: 'Status',
    defaultWidth: 110,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.CURRENT_LOCATION,
    field: 'CurrentLocation',
    label: 'Location',
    defaultWidth: 150,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.DAYS,
    field: 'DaysInEvidence',
    label: 'Days',
    defaultWidth: 90,
    sortable: true,
  },
  {
    id: DISPOSAL_COLUMN_IDS.MATCHED_RULES,
    field: '_matchedRules',
    label: 'Matched Rule(s)',
    defaultWidth: 180,
    sortable: false,
  },
  {
    id: DISPOSAL_COLUMN_IDS.REQUIRED_ACTION,
    field: 'RequiredAction',
    label: 'Required Action',
    defaultWidth: 160,
    sortable: true,
  },
];

export const DISPOSAL_DEFAULT_COLUMN_ORDER = DISPOSAL_COLUMNS.map((column) => column.id);

export const DISPOSAL_RESULTS_PRINT_COLUMNS = [
  { field: 'EvidenceID', label: 'Evidence ID Barcode', type: 'barcode' },
  { field: 'EvidenceID', label: 'Evidence ID' },
  { field: 'IncidentID', label: 'Incident Number' },
  { field: 'Description', label: 'Description' },
  { field: 'Category', label: 'Category' },
  { field: 'Status', label: 'Status' },
  { field: 'CurrentLocation', label: 'Location' },
  { field: 'DaysInEvidence', label: 'Days' },
  { field: 'matchedRules', label: 'Matched Rule(s)', type: 'chips' },
  { field: 'RequiredAction', label: 'Required Action' },
];

export const DISPOSAL_FILTER_ADVANCED_KEY = 'advanced';

export const DEFAULT_ADVANCED_SEARCH = {
  EvidenceID: [],
  IncidentID: [],
  TagNumber: [],
  CollectedBy: [],
  LocationCollected: [],
  Make: [],
  Model: [],
  SerialNumber: [],
  VIN: [],
  LicenseNumber: [],
  SubstanceCategory: [],
  DrugType: [],
  StorageLocation: [],
  SubLocation: [],
  Container: [],
  CollectedDate: { from: null, to: null },
  FinalDisposition: [],
  DispositionDate: { from: null, to: null },
  PartyType: [],
  PartyName: '',
};

// Quick-search box: substring match against these evidence item fields.
export const QUICK_SEARCH_FIELDS = ['EvidenceID', 'IncidentID', 'Description'];

// Toolbar multi-select filters: filter key -> evidence item field. 1:1 PascalCase
// mapping, same convention as the category/status/currentLocation/requiredAction
// fields below.
export const MULTI_SELECT_FILTER_FIELDS = {
  category: 'CategoryID',
  propertyType: 'PropertyTypeID',
  evidenceType: 'EvidenceTypeID',
  status: 'StatusID',
  tempLocation: 'TempLocationID',
  currentLocation: 'CurrentLocation',
  submittedBy: 'SubmittedBy',
  requiredAction: 'RequiredActionID',
  storageLocation: 'StorageLocationID',
};

// DisposalFilterToolbar's multi-select fields: name/label/dropdownKey — options are resolved at
// render time via dropdowns[dropdownKey] (same pattern as RULE_FIELD_CONFIG above).
export const TOOLBAR_MULTI_SELECT_FIELD_CONFIG = [
  { name: 'category', label: 'Category', dropdownKey: 'category' },
  { name: 'propertyType', label: 'Property Type', dropdownKey: 'propertyType' },
  { name: 'evidenceType', label: 'Evidence Type', dropdownKey: 'evidenceType' },
  { name: 'status', label: 'Status', dropdownKey: 'custodyStatus' },
  { name: 'tempLocation', label: 'Temp Location', dropdownKey: 'tempLocation' },
  { name: 'requiredAction', label: 'Required Actions', dropdownKey: 'requiredAction' },
  { name: 'storageLocation', label: 'Storage Location', dropdownKey: 'storageLocation' },
];

export const ADVANCED_MULTI_SELECT_FILTER_FIELDS = {
  SubstanceCategory: 'SubstanceCategoryID',
  DrugType: 'DrugTypeID',
  StorageLocation: 'StorageLocationID',
  SubLocation: 'SubLocationID',
  Container: 'ContainerID',
  FinalDisposition: 'FinalDispositionCodeID',
};

export const SUB_LOCATION_DROPDOWN_KEY = 'subLocation';

// DisposalAdvancedSearch's rendered multi-select fields: name/label/dropdownKey — options are
// resolved at render time via dropdowns[dropdownKey], except SubLocation, whose options are the
// Storage-Location-cascaded list computed in-component.
export const ADVANCED_SEARCH_MULTI_SELECT_FIELD_CONFIG = [
  {
    name: 'advanced.SubstanceCategory',
    label: 'Substance Category',
    dropdownKey: 'substanceCategory',
  },
  { name: 'advanced.DrugType', label: 'Drug Type', dropdownKey: 'drugType' },
  { name: 'advanced.StorageLocation', label: 'Storage Location', dropdownKey: 'storageLocation' },
  { name: 'advanced.SubLocation', label: 'Sub-Location', dropdownKey: SUB_LOCATION_DROPDOWN_KEY },
  { name: 'advanced.Container', label: 'Container', dropdownKey: 'container' },
];

export const ADVANCED_DATE_RANGE_FILTER_FIELDS = {
  CollectedDate: 'DateTimeCollected',
  DispositionDate: 'DispositionDate',
};

export const DEFAULT_DISPOSAL_FILTERS = {
  search: '',
  submittedDate: { from: null, to: null },
  category: [],
  propertyType: [],
  evidenceType: [],
  status: [],
  tempLocation: [],
  currentLocation: [],
  submittedBy: [],
  requiredAction: [],
  storageLocation: [],
  advanced: DEFAULT_ADVANCED_SEARCH,
};

export const REQUIRED_ACTION_NONE = 'None';

export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
};

export const EMPTY_BULK_DISPOSITION = {
  [EVIDENCE_DISPOSITION_FIELDS.finalDispositionId]: null,
  [EVIDENCE_DISPOSITION_FIELDS.finalDispositionLabel]: '',
  [EVIDENCE_DISPOSITION_FIELDS.dispositionDate]: null,
  [EVIDENCE_DISPOSITION_FIELDS.disposedBy]: '',
  [EVIDENCE_DISPOSITION_FIELDS.disposedById]: null,
  [EVIDENCE_DISPOSITION_FIELDS.howDestroyedId]: null,
  [EVIDENCE_DISPOSITION_FIELDS.howDestroyedLabel]: '',
  [EVIDENCE_DISPOSITION_FIELDS.destructionDate]: null,
  [EVIDENCE_DISPOSITION_FIELDS.destructionReturnDate]: null,
  [EVIDENCE_DISPOSITION_FIELDS.returnedBy]: '',
  [EVIDENCE_DISPOSITION_FIELDS.returnedById]: null,
  [EVIDENCE_DISPOSITION_FIELDS.returnedDate]: null,
  [EVIDENCE_DISPOSITION_FIELDS.returnedTo]: '',
  [EVIDENCE_DISPOSITION_FIELDS.returnedToId]: null,
  [EVIDENCE_DISPOSITION_FIELDS.releasedTo]: '',
  [EVIDENCE_DISPOSITION_FIELDS.releasedToId]: null,
  [EVIDENCE_DISPOSITION_FIELDS.dispositionNotes]: '',
};
